import { useContext } from 'react';
import AppContext from '../../../AppContext';
import QtrReinsurersRowData from './QtrReinsurersRowData';
import styles from '../RollingRow/RollingRow.module.css';
import { DataResult } from '../../model/DataResult';
import RowHelmet from '../RowHelmet';
import LabelRow from '../LabelRow';
import BlankRow from '../BlankRow/BlankRow';
import classNames from 'classnames/bind';

const cx = classNames.bind(styles);

const DEFAULT_VALUE = '-';

function rowClassSwitch(rowClass: string = '') {
  switch (rowClass) {
    case 'INDENT':
      return 'indent';
    case 'TOTAL':
      return 'total';
    case 'GRAND_TOTAL':
      return 'grand_total';
    case 'BOLD':
      return 'bold';
    default:
      return '';
  }
}

function getQuarterValues(data: QtrReinsurersRowData): (DataResult | undefined)[] {
  return [
    data.currentQuarter,
    data.priorQuarter,
    data.priorQuarterMinusOne,
    data.priorQuarterMinusTwo,
    data.priorQuarterMinusThree
  ];
}

function hasAnyOutsideBenchmarks(data: QtrReinsurersRowData) {
  return getQuarterValues(data).some((v) => v?.outsideBenchmark);
}

/**
 * Format a quarterly count value for display as a whole number.
 * Returns the default placeholder ('-') when the value is null/undefined.
 */
function formatCount(result?: DataResult): string {
  const value = result?.value;
  if (value === null || value === undefined || value === '') {
    return DEFAULT_VALUE;
  }
  const numValue = typeof value === 'string' ? Number.parseFloat(value) : (value as number);
  if (Number.isNaN(numValue)) {
    return DEFAULT_VALUE;
  }
  return Math.round(numValue).toLocaleString('en-US');
}

function getDataDisplayForCurrentRepository(
  riskRepository: string | null,
  rowDisplay: QtrReinsurersRowData['rowDisplay']
) {
  if (riskRepository === 'FR' || !rowDisplay || !riskRepository) {
    return '';
  }
  const displayValue = rowDisplay[riskRepository.toLowerCase() as keyof typeof rowDisplay];
  if (displayValue === 'GREY') return 'grey';
  if (displayValue === 'HIDE') return 'hidden';
  return '';
}

export default function QtrReinsurersRow({
  sectionKey,
  subSectionKey,
  rowKey,
  rowType,
  rowClass,
  label,
  benchmark,
  rowDisplay,
  calculateDetail,
  rowDataSource,
  rowLinkConfiguration,
  currentQuarter,
  priorQuarter,
  priorQuarterMinusOne,
  priorQuarterMinusTwo,
  priorQuarterMinusThree
}: Readonly<QtrReinsurersRowData>) {
  const { showBenchmark, riskRepository } = useContext(AppContext);

  if (!label || label.trim().toLowerCase() === 'missing') {
    return null;
  }

  if (rowType === 'BLANK') {
    return <BlankRow />;
  }

  const repoDisplay = getDataDisplayForCurrentRepository(riskRepository, rowDisplay);

  const className = cx(
    'container',
    { collapse: !showBenchmark },
    repoDisplay,
    rowClassSwitch(rowClass)
  );

  if (rowType === 'LABEL') {
    const id =
      'label-' +
      label
        .replace(/\s+/g, '-')
        .replace(/[^a-zA-Z0-9-]+/g, '')
        .toLowerCase();
    return (
      <div id={id} data-testid={id} className={className}>
        <RowHelmet label={label} rowLinkConfiguration={rowLinkConfiguration} />
        <LabelRow />
      </div>
    );
  }

  const data: QtrReinsurersRowData = {
    rowKey,
    rowType,
    rowClass,
    label,
    benchmark,
    rowDisplay,
    calculateDetail,
    rowDataSource,
    rowLinkConfiguration,
    currentQuarter,
    priorQuarter,
    priorQuarterMinusOne,
    priorQuarterMinusTwo,
    priorQuarterMinusThree
  };

  const quarterValues = getQuarterValues(data);
  const id = sectionKey ? `${sectionKey}-${rowKey}` : `rowKey-${rowKey}`;

  return (
    <div id={id} data-testid="qtr-reinsurers-data-row" className={className}>
      <RowHelmet
        label={label}
        rowKey={rowKey}
        isOutsideBenchmark={hasAnyOutsideBenchmarks(data)}
        benchmarkText={benchmark}
        calculateDetail={calculateDetail}
        rowType={rowType}
        rowDataSource={rowDataSource}
        rowLinkConfiguration={rowLinkConfiguration}
        sectionKey={sectionKey}
        subSectionKey={subSectionKey}
      />
      {/* Empty placeholder columns (Pct Change, $ Change, Surplus, NAA) */}
      <p></p>
      <p></p>
      <p></p>
      <p></p>
      {/* 5 rolling quarter count columns */}
      {quarterValues.map((q, i) => (
        <p key={i} className="column_align_right">
          {formatCount(q)}
        </p>
      ))}
    </div>
  );
}
